import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

const setHead = (tag, attrs) => {
  const sel = Object.entries(attrs).filter(([k]) => k === "rel" || k === "name").map(([k, v]) => `[${k}="${v}"]`).join("");
  let el = document.head.querySelector(`${tag}${sel}`);
  const created = !el;
  if (created) { el = document.createElement(tag); document.head.appendChild(el); }
  const prev = {};
  Object.entries(attrs).forEach(([k, v]) => { prev[k] = el.getAttribute(k); el.setAttribute(k, v); });
  return () => { if (created) el.remove(); else Object.entries(prev).forEach(([k, v]) => (v == null ? el.removeAttribute(k) : el.setAttribute(k, v))); };
};

export const PwaInstall = ({ manifest, sw, scope, iconDir, appleIcon, appTitle, subtitle, dismissKey, testId }) => {
  const [prompt, setPrompt] = useState(null);
  const [ios, setIos] = useState(false);
  const [hidden, setHidden] = useState(() => localStorage.getItem(dismissKey) === "1");

  useEffect(() => {
    const undo = [
      setHead("link", { rel: "manifest", href: manifest }),
      setHead("link", { rel: "apple-touch-icon", href: appleIcon || `${iconDir}/apple-touch-icon.png` }),
      setHead("meta", { name: "theme-color", content: "#f59e0b" }),
      setHead("meta", { name: "apple-mobile-web-app-capable", content: "yes" }),
      setHead("meta", { name: "apple-mobile-web-app-title", content: appTitle }),
    ];
    if ("serviceWorker" in navigator) navigator.serviceWorker.register(sw, { scope }).catch(() => {});
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    setIos(!standalone && /iphone|ipad|ipod/i.test(navigator.userAgent));
    return () => { undo.forEach((f) => f()); window.removeEventListener("beforeinstallprompt", onPrompt); };
  }, [manifest, sw, scope, iconDir, appleIcon, appTitle]);

  const close = () => { localStorage.setItem(dismissKey, "1"); setHidden(true); };
  const install = async () => { prompt.prompt(); await prompt.userChoice.catch(() => {}); setPrompt(null); };
  if (hidden || (!prompt && !ios)) return null;

  return (
    <div className="fixed inset-x-3 bottom-3 z-50 rounded-xl border border-amber-300 bg-white p-3 shadow-xl flex items-center gap-3 sm:max-w-md sm:left-auto" data-testid={`${testId}-banner`}>
      <img src={`${iconDir}/icon-192.png`} alt="" className="h-10 w-10 rounded-lg shrink-0" />
      <div className="flex-1 min-w-0 text-sm text-[#111827]">
        <div className="font-semibold">Aggiungi a schermata Home</div>
        {prompt ? <div className="text-xs text-slate-600">{subtitle}</div>
          : <div className="text-xs text-slate-600 flex flex-wrap items-center gap-1">Tocca <Share className="h-3.5 w-3.5" /> Condividi, poi "Aggiungi a Home".</div>}
      </div>
      {prompt && (
        <button type="button" onClick={install} data-testid={`${testId}-install`}
          className="inline-flex items-center gap-1 rounded-lg bg-amber-500 hover:bg-amber-600 px-3 py-2 text-xs font-semibold text-[#0a0d14] transition-colors">
          <Download className="h-3.5 w-3.5" />Installa
        </button>
      )}
      <button type="button" onClick={close} title="Chiudi" data-testid={`${testId}-dismiss`} className="p-1 rounded-md text-slate-500 hover:bg-slate-100 transition-colors"><X className="h-4 w-4" /></button>
    </div>
  );
};

const useStoredTenant = (key, evt) => {
  const [tid, setTid] = useState(() => localStorage.getItem(key));
  useEffect(() => {
    const on = () => setTid(localStorage.getItem(key));
    window.addEventListener(evt, on);
    return () => window.removeEventListener(evt, on);
  }, [key, evt]);
  return tid;
};

export const SegnalatoriPwa = () => {
  const tid = useStoredTenant("pgp_seg_tenant", "pgp-seg-tenant");
  const base = tid ? `/api/segnalatori-pwa/${tid}` : null;
  return (
    <PwaInstall manifest={base ? `${base}/manifest.webmanifest` : "/segnalatori.webmanifest"} sw="/segnalatori-sw.js" scope="/segnalatori" iconDir="/segnalatori"
      appleIcon={base ? `${base}/apple-touch-icon.png` : undefined}
      appTitle="Area Segnalatori" subtitle="Apri l'Area Segnalatori come un'app." dismissKey="pgp_seg_pwa_dismissed" testId="seg-pwa" />
  );
};

export const ClientePwa = () => {
  const tid = useStoredTenant("pgp_cli_tenant", "pgp-cli-tenant");
  const base = tid ? `/api/cliente-pwa/${tid}` : null;
  return (
    <PwaInstall manifest={base ? `${base}/manifest.webmanifest` : "/cliente.webmanifest"} sw="/cliente-sw.js" scope="/cliente" iconDir="/pwa-cliente"
      appleIcon={base ? `${base}/apple-touch-icon.png` : undefined}
      appTitle="Area Cliente" subtitle="Apri la tua Area Riservata come un'app." dismissKey="pgp_cli_pwa_dismissed" testId="cli-pwa" />
  );
};
